import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'

import content from 'prismic-translates/dialog-daily-reward.json'

import type { SnackbarVariants } from '@patrianna/shared-patrianna-types/store/SnackbarModule'
import type {
  SectionType,
  SectionDetails,
  GoldSweepstakeCoinsDetails,
  FreeSpinsDetails,
  PurchaseOfferDetails,
  PrizeDrawDetails,
} from 'src/store/modules/bonusesFlow/types'

const tPrismic = getTranslationFromJson([content])

export const SNACKBAR_MESSAGES = {
  FREE_SPINS_ERROR: 'key.campaign.error',
  FREE_SPINS_UNAVAILABLE: 'key.campaign.unavailable',
  FREE_SPINS_NOT_AWARDED: 'free_spins_not_awarded',
  PURCHASE_OFFER_ERROR: 'key.offer.error',
  PURCHASE_OFFER_NOT_AWARDED: 'key.offer.unavailable',
} as const

export const snackbarMessage: Record<string, { variant: SnackbarVariants; message: string }> = {
  [SNACKBAR_MESSAGES.FREE_SPINS_ERROR]: {
    message: tPrismic('fs_assignment_was_failed'),
    variant: 'error',
  },
  [SNACKBAR_MESSAGES.FREE_SPINS_UNAVAILABLE]: {
    message: tPrismic('fs_assignment_was_failed'),
    variant: 'error',
  },
  [SNACKBAR_MESSAGES.FREE_SPINS_NOT_AWARDED]: {
    message: tPrismic('fs_assignment_pending'),
    variant: 'info',
  },
  [SNACKBAR_MESSAGES.PURCHASE_OFFER_ERROR]: {
    message: tPrismic('purchase_offer_not_awarded'),
    variant: 'error',
  },
  [SNACKBAR_MESSAGES.PURCHASE_OFFER_NOT_AWARDED]: {
    message: tPrismic('purchase_offer_not_awarded'),
    variant: 'error',
  },
}

interface RewardSectionConfig {
  title: string
  getSubtitle: (
    details: GoldSweepstakeCoinsDetails | FreeSpinsDetails | PurchaseOfferDetails | PrizeDrawDetails,
  ) => string
}

export const ERROR_REWARD_CONFIG = {
  title: tPrismic('wow_error_title'),
  subtitle: tPrismic('wow_unexpected_error_subtitle'),
}

function formatToThousands(value: number): string {
  return value >= 10000 ? `${value / 1000}k` : `${value}`
}

const rewardSectionConfig: Record<SectionType, RewardSectionConfig> = {
  gold_sweepstake_coins: {
    title: tPrismic('wow_coins_won_title'),
    getSubtitle: (details: GoldSweepstakeCoinsDetails) => {
      const gcFormatted = formatToThousands(details.gcAmount)

      if (details.scAmount > 0) {
        return `GC ${gcFormatted} + free SC ${details.scAmount}`
      }

      return `GC ${gcFormatted}`
    },
  },
  free_spins: {
    title: tPrismic('wow_free_spins_won_title'),
    getSubtitle: (details: FreeSpinsDetails) => `${details.value} ${tPrismic('wow_free_spins_won_subtitle')}`,
  },
  purchase_offer: {
    title: tPrismic('wow_purchase_offer_won_title'),
    getSubtitle: (details: PurchaseOfferDetails) => `${details.value}% ${tPrismic('wow_purchase_offer_won_subtitle')}`,
  },
  prize_draw: {
    title: tPrismic('wow_prize_draw_won_title'),
    getSubtitle: (details: PrizeDrawDetails) => `${details.value} ${tPrismic('wow_prize_draw_won_subtitle')}`,
  },
}

export const getRewardConfig = (sectionDetails: SectionDetails, isTopPrize = false) => {
  const config = sectionDetails && rewardSectionConfig[sectionDetails.type]

  if (!config) {
    return ERROR_REWARD_CONFIG
  }

  return {
    title: isTopPrize ? tPrismic('wow_top_prize_title') : config.title,
    subtitle: config.getSubtitle(sectionDetails.details),
  }
}

// Get button text for each section type
export const getButtonClaimText = (sectionType?: SectionType): string => {
  if (!sectionType) {
    return tPrismic('wow_claim_default_btn')
  }

  const buttonTexts: Record<SectionType, string> = {
    gold_sweepstake_coins: tPrismic('wow_claim_coins_btn'),
    prize_draw: tPrismic('wow_claim_prize_draw_btn'),
    free_spins: tPrismic('wow_claim_free_spins_btn'),
    purchase_offer: tPrismic('wow_claim_offer_btn'),
  }

  return buttonTexts[sectionType] || tPrismic('wow_claim_default_btn')
}

const ERROR_MESSAGE_KEYS = {
  freeSpins: [SNACKBAR_MESSAGES.FREE_SPINS_ERROR, SNACKBAR_MESSAGES.FREE_SPINS_UNAVAILABLE],
  purchaseOffer: [SNACKBAR_MESSAGES.PURCHASE_OFFER_ERROR, SNACKBAR_MESSAGES.PURCHASE_OFFER_NOT_AWARDED],
}

const hasError = (hasWin: boolean, messageKey: string | undefined, validMessages: string[]): boolean => {
  return hasWin && !!messageKey && validMessages.includes(messageKey)
}

// Check if we should show error state for free spins
export const shouldShowFreeSpinsError = (hasFreeSpinsWin: boolean, messageKey?: string): boolean => {
  return hasError(hasFreeSpinsWin, messageKey, ERROR_MESSAGE_KEYS.freeSpins)
}

// Check if we should show error state for purchase offers
export const shouldShowPurchaseOfferError = (hasPurchaseOfferWin: boolean, messageKey?: string): boolean => {
  return hasError(hasPurchaseOfferWin, messageKey, ERROR_MESSAGE_KEYS.purchaseOffer)
}

// Check if we should show any error state
export const shouldShowErrorState = (
  hasFreeSpinsWin: boolean,
  hasPurchaseOfferWin: boolean,
  messageKey?: string,
): boolean => {
  return (
    shouldShowFreeSpinsError(hasFreeSpinsWin, messageKey) ||
    shouldShowPurchaseOfferError(hasPurchaseOfferWin, messageKey)
  )
}

// Get modified reward config for error state
export const getDisplayRewardConfig = (
  sectionDetails: SectionDetails,
  hasFreeSpinsWin: boolean,
  hasPurchaseOfferWin: boolean,
  messageKey?: string,
  isTopPrizeWon = false,
) => {
  return shouldShowErrorState(hasFreeSpinsWin, hasPurchaseOfferWin, messageKey)
    ? { title: tPrismic('wow_error_title'), subtitle: tPrismic('wow_error_subtitle') }
    : getRewardConfig(sectionDetails, isTopPrizeWon)
}

export const getButtonText = (
  sectionDetails: SectionDetails,
  hasFreeSpinsWin: boolean,
  hasPurchaseOfferWin: boolean,
  messageKey?: string,
): string => {
  return shouldShowErrorState(hasFreeSpinsWin, hasPurchaseOfferWin, messageKey)
    ? tPrismic('wow_claim_error_btn')
    : getButtonClaimText(sectionDetails?.type)
}
